import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Fragment, useMemo, useState } from 'react';

export interface AppointmentOption {
  id: string;
  appointmentCode?: string;
  scheduledDate?: string;
  clientName?: string;
  vehiclePlate?: string;
  serviceType?: string;
}

interface AppointmentComboboxProps {
  appointments: AppointmentOption[];
  selected: AppointmentOption | null;
  onSelect: (appointment: AppointmentOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

// Formatea YYYY-MM-DD como dd/mm/yy SIN pasar por Date (new Date('2026-07-06')
// se interpreta como UTC y en Argentina retrocede un día)
const formatFecha = (value?: string) => {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const appointmentLabel = (apt: AppointmentOption) => {
  // Fecha al final: cuando un mismo auto tiene varias OTs, se distingue por recencia
  const fecha = formatFecha(apt.scheduledDate);
  return [
    apt.appointmentCode || fecha || 'Turno',
    apt.vehiclePlate,
    apt.clientName,
    apt.appointmentCode ? fecha : ''
  ].filter(Boolean).join(' · ');
};

// Combobox de OTs: filtra escribiendo por codigo (OT-0068), patente o cliente
export default function AppointmentCombobox({
  appointments,
  selected,
  onSelect,
  placeholder = 'Buscar OT por código, patente o cliente...',
  disabled = false
}: AppointmentComboboxProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return appointments;
    return appointments.filter((apt) => {
      const code = String(apt.appointmentCode || '').toLowerCase();
      const plate = String(apt.vehiclePlate || '').toLowerCase();
      const client = String(apt.clientName || '').toLowerCase();
      const service = String(apt.serviceType || '').toLowerCase();
      return (
        code.includes(normalized) ||
        plate.includes(normalized) ||
        client.includes(normalized) ||
        service.includes(normalized)
      );
    });
  }, [appointments, query]);

  return (
    <Combobox value={selected} onChange={onSelect} disabled={disabled} by="id">
      {({ open }) => (
      /* z-[60] SOLO abierto: por encima de ProductCombobox (z-50) sin tapar
         los dropdowns de otros selects cuando está cerrado */
      <div className={`relative ${open ? 'z-[60]' : ''}`}>
        <div className="relative">
          <Combobox.Input
            className="w-full h-10 px-4 pl-10 pr-14 border border-input rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={placeholder}
            displayValue={(apt: AppointmentOption | null) => (apt ? appointmentLabel(apt) : '')}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {selected && !disabled && (
            <button
              type="button"
              onClick={() => { onSelect(null); setQuery(''); }}
              className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Quitar OT"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <Combobox.Button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg shadow-lg border border-border bg-card text-foreground">
          {filtered.length === 0 && (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <p className="font-medium mb-1">No se encontraron OTs</p>
              <p className="text-sm">Probá con el código (OT-...), la patente o el cliente</p>
            </div>
          )}

          {filtered.map((apt) => (
            <Combobox.Option key={apt.id} value={apt} as={Fragment}>
              {({ active, selected: isSelected }) => (
                <li className={`relative cursor-pointer select-none px-4 py-2 transition-colors ${active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${isSelected ? 'font-semibold' : 'font-normal'}`}>{appointmentLabel(apt)}</span>
                    {isSelected && <CheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" />}
                  </div>
                </li>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </div>
      )}
    </Combobox>
  );
}
